import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ArrowRight,
  Shield,
  CreditCard,
  CheckCircle2,
  Home,
  Layers,
} from "lucide-react";

export default function SubscriptionSummary({
  isSubscribed,
  status,
  enabledPaidCount,
  totalPaidCount,
  perServicePrice,
  homeCount,
  onSubscribe,
  onManage,
  onCancel,
  actionLoading,
  paidServices = [],
  previewToggles = {},
  onToggle,
}) {
  const monthlyTotal = enabledPaidCount * perServicePrice * Math.max(homeCount, 1);
  const plan = status?.plans?.basic;
  const priceLabel = plan ? `$${(plan.priceInCents / 100).toFixed(0)}` : `$${monthlyTotal || 0}`;
  const displayMax = Math.max(totalPaidCount, enabledPaidCount);
  const progressPct = displayMax > 0 ? Math.min(100, Math.round((enabledPaidCount / displayMax) * 100)) : 0;

  if (isSubscribed) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-6 shadow-bellhopt">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 size={17} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Your Subscription</h3>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 uppercase tracking-wide">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              {status?.cancelAtPeriodEnd ? "Canceling" : "Active"}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          <SummaryRow label="Plan" value={`${priceLabel}/mo`} />
          <SummaryRow label="Active services" value={`${enabledPaidCount} of ${displayMax}`} />
          <SummaryRow label="Properties" value={String(homeCount)} />
          {status?.currentPeriodEnd && (
            <SummaryRow
              label={status.cancelAtPeriodEnd ? "Active until" : "Next billing"}
              value={new Date(status.currentPeriodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            />
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex justify-between text-[11px] text-gray-400 mb-1.5">
            <span>Services enabled</span>
            <span className="font-semibold text-gray-600">{enabledPaidCount}/{displayMax}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>

        <div className="h-px bg-gray-100 mb-4" />

        <div className="space-y-2">
          <button
            onClick={onManage}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-bellhopt disabled:opacity-60"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CreditCard size={14} />
                Manage Billing
              </>
            )}
          </button>
          {!status?.cancelAtPeriodEnd && (
            <button
              onClick={onCancel}
              disabled={actionLoading}
              className="w-full py-2 text-sm text-gray-400 hover:text-red-500 transition-colors disabled:opacity-60"
            >
              Cancel subscription
            </button>
          )}
        </div>

        {status?.cancelAtPeriodEnd && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-800">
              Services remain active until{" "}
              <strong>
                {new Date(status.currentPeriodEnd).toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                })}
              </strong>.
            </p>
          </div>
        )}
      </div>
    );
  }

  const canSubscribe = enabledPaidCount > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-bellhopt">
      <h3 className="text-lg font-bold text-gray-900 mb-1">Choose Your Services</h3>
      <p className="text-sm text-gray-400 mb-5">
        Toggle the services you want, then subscribe to go live.
      </p>

      {/* Service toggles — 2x2 grid */}
      {paidServices.length > 0 && onToggle && (
        <div className="grid grid-cols-2 gap-2 mb-6">
          {paidServices.map((svc) => {
            const on = !!previewToggles[svc.key];
            return (
              <button
                key={svc.key}
                onClick={() => onToggle(svc.key, !on)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border transition-all text-left ${
                  on
                    ? "bg-white border-primary-200 shadow-sm"
                    : "bg-gray-50/60 border-gray-100 hover:border-gray-200"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${
                    on ? "bg-primary-500" : "bg-gray-300"
                  }`}
                >
                  {on && (
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-xs font-medium leading-tight block ${on ? "text-gray-900" : "text-gray-500"}`}>
                    {svc.name}
                  </span>
                  <span className="text-[10px] font-semibold text-emerald-600">
                    +${perServicePrice}/home
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="h-px bg-gray-100 mb-4" />

      {/* Pricing breakdown */}
      <div className="space-y-3 mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Layers size={14} className="text-gray-400" />
            Paid services
          </div>
          <AnimatePresence mode="wait">
            <motion.span
              key={enabledPaidCount}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              className="text-sm font-semibold text-gray-900"
            >
              {enabledPaidCount}
            </motion.span>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-xs text-gray-400">x</span>
            Per service/home
          </div>
          <span className="text-sm font-semibold text-gray-900">${perServicePrice}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Home size={14} className="text-gray-400" />
            Properties
          </div>
          <span className="text-sm font-semibold text-gray-900">{Math.max(homeCount, 1)}</span>
        </div>
      </div>

      <div className="h-px bg-gray-100 mb-4" />

      <div className="flex items-baseline justify-between mb-6">
        <span className="text-sm font-medium text-gray-600">Estimated monthly</span>
        <AnimatePresence mode="wait">
          <motion.div
            key={monthlyTotal}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-right"
          >
            <span className="text-3xl font-extrabold text-gray-900 tracking-tight">
              ${monthlyTotal}
            </span>
            <span className="text-sm text-gray-400 font-medium">/mo</span>
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        onClick={() => onSubscribe("basic", "summary")}
        disabled={actionLoading || !canSubscribe}
        className={`w-full flex items-center justify-center gap-2 py-3.5 text-sm font-bold rounded-xl transition-colors shadow-bellhopt disabled:opacity-60 ${
          canSubscribe
            ? "bg-primary-500 text-white hover:bg-primary-600"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        {actionLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            {canSubscribe ? "Subscribe" : "Select at least 1 service"}
            {canSubscribe && <ArrowRight size={14} strokeWidth={2.5} />}
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-4 mt-3">
        <span className="flex items-center gap-1 text-[11px] text-gray-400">
          <Shield size={10} />
          Cancel anytime
        </span>
        <span className="flex items-center gap-1 text-[11px] text-gray-400">
          <CreditCard size={10} />
          Secure checkout
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}
